import React, { Suspense, lazy } from "react";
import { createFileRoute, useParams } from "@tanstack/react-router";
import SiteHeader from "@/components/common/SiteHeader";
import { useRdpLL97SubmissionQuery } from "@/queries/queries";
import { Button } from "@/components/shared/ui/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/base";
import { toast } from "@/queries/useToast";
import { Loader2, AlertTriangle } from "lucide-react";
import CalendarYearPill from "@/components/common/CalendarYearPill";

// Wave-17 Item 1.1 — RDP LL97 submission page lazy-loaded.
const LL97Page = lazy(() => import("@/pages/RDP/LL97/Submission/Page"));

// Narrow shape of the axios error we care about: backend errors are
// JSON-bodied and include a `message` field. Anything else falls through
// to the static fallback below.
type ConfirmErrorLike = {
  response?: { data?: { message?: string } };
};

const RDP_CONSENT_TOOLTIP =
  "Re-confirm your RDP acceptance for this calendar year to continue.";

export const LL97Route = () => {
  const { submissionId } = useParams({ from: "/rdp/ll97/$submissionId" });
  const { data } = useRdpLL97SubmissionQuery(submissionId);
  const submission = data?.submission ?? data;
  const year: number | undefined = submission?.calendarYear;
  // Boolean flag from the server-side LL97 submission. Backend contract:
  // `rdp_confirmed_for_current_year` (camelized on the client). When this
  // is explicitly false, the RDP must re-confirm before they can certify.
  // We use an explicit === false check so a missing/undefined field (old
  // backend) does not accidentally gate the page.
  const needsReconsent = submission?.rdpConfirmedForCurrentYear === false;

  const queryClient = useQueryClient();
  const confirmMutation = useMutation({
    // Backend endpoint: `POST /api/audits/ll97/{id}/confirm_rdp_for_year/`.
    // TODO(backend): wire this endpoint if not yet added.
    mutationFn: () =>
      api.post(`/audits/ll97/${submissionId}/confirm_rdp_for_year/`),
    onSuccess: () => {
      toast({
        title: "Confirmed",
        description: "RDP acceptance recorded for this calendar year.",
      });
      queryClient.invalidateQueries({
        queryKey: ["rdpLL97Submission", submissionId],
      });
      queryClient.invalidateQueries({
        queryKey: ["ll97Submission", submissionId],
      });
    },
    onError: (err: ConfirmErrorLike) => {
      toast({
        title: "Error",
        description:
          err?.response?.data?.message ??
          "Failed to record RDP acceptance. Please retry.",
        variant: "destructive",
      });
    },
  });

  return (
    <div className="flex flex-col w-full min-h-full">
      <SiteHeader header="LL97" />
      <div className="flex items-center gap-3 px-10 pt-4">
        <CalendarYearPill year={year} />
      </div>
      {needsReconsent && (
        <div
          role="alert"
          className="mx-10 mt-4 rounded-md border border-yellow-500 bg-yellow-50 p-4 flex items-start gap-3"
        >
          <AlertTriangle
            className="h-5 w-5 text-yellow-700 mt-0.5 shrink-0"
            aria-hidden
          />
          <div className="flex-1">
            <div className="font-semibold text-yellow-900">
              Action required: Re-confirm your RDP acceptance
              {typeof year === "number" ? ` for CY${year}` : ""}
            </div>
            <div className="text-sm text-yellow-900 mt-1">
              Certification and submit actions are disabled until you confirm.
            </div>
          </div>
          <Button
            onClick={() => confirmMutation.mutate()}
            disabled={confirmMutation.isPending}
          >
            {confirmMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Accept
          </Button>
        </div>
      )}
      <div
        className={needsReconsent ? "relative" : undefined}
        title={needsReconsent ? RDP_CONSENT_TOOLTIP : undefined}
      >
        <div
          aria-disabled={needsReconsent || undefined}
          className={
            needsReconsent ? "pointer-events-none opacity-60" : undefined
          }
        >
          <Suspense fallback={null}>
            <LL97Page />
          </Suspense>
        </div>
      </div>
    </div>
  );
};

export const Route = createFileRoute("/rdp/ll97/$submissionId")({
  component: LL97Route,
});
